import React, { Component, useRef } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { withTranslation } from 'react-i18next'
import _ from 'lodash'

import { Button, Card, Grid, List, Modal, Popconfirm, Table, Tag, Typography } from 'antd'
import { LinkOutlined, DeleteOutlined, CopyOutlined, InfoCircleOutlined } from '@ant-design/icons'

import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import LineItemItemTruckImagesContainer from '../../containers/LineItems/ItemTruckImages'
import UploadItemTruckImageContainer from '../../containers/LineItems/UploadItemTruckImage'
import EditableCell from '../../containers/Inquiry/EditableCell'
import ItemPricesContainer from '../../containers/Inquiry/ItemPrices'
import ItemProductImage from './ItemProductImage'
import LineItemsWrapper from './lineItems.style'

import apiConfig from '../../../config/apiConfig'
import inquiryActions from '../../redux/inquiry/actions'
import ItemState from './itemState'

const {
    deleteOrderLineItem,
    movePositionLineItem,
    toggleItemPricesModal
} = inquiryActions

const EditableContext = React.createContext()
let dragingIndex = -1

const flattenLineItems = (lineItems) => {
    return lineItems.reduce((acc, lineItem) => {
        acc.push(lineItem)
        const { service_line_items } = lineItem || {}
        if (service_line_items && service_line_items.length > 0) {
            acc = acc.concat(service_line_items)
        }
        return acc
    }, [])
}

const isServiceLineItemRow = (children) => {
    const firstChild = children && children[0]
    const record = (firstChild && firstChild.props && firstChild.props.record) || null
    return record && record.type === 'service_line_item'
}

const DragableBodyRow = ({ moveRow, form, index, ...restProps }) => {
    const ref = useRef(null)
    const isService = isServiceLineItemRow(restProps.children)

    const [{ isOver }, dropConnect] = useDrop({
        accept: 'row',
        canDrop: () => !isService,
        collect: (monitor) => ({ isOver: monitor.isOver() }),
        drop: (item) => {
            if (item.index === index) return
            if (moveRow) moveRow(item.index, index)
            item.index = index
        }
    })

    const [, dragConnect] = useDrag({
        type: 'row',
        canDrag: () => !isService,
        item: () => {
            dragingIndex = index
            return { index }
        }
    })

    dragConnect(dropConnect(ref))

    const style = { ...restProps.style, cursor: 'move' }
    let className = restProps.className || ''

    if (isOver) {
        if (index > dragingIndex) className += ' drop-over-downward'
        if (index < dragingIndex) className += ' drop-over-upward'
    }

    return (
        <EditableContext.Provider value={form}>
            <tr {...restProps} ref={ref} className={className} style={style} />
        </EditableContext.Provider>
    )
}

class DragSortingTable extends Component {
    static propTypes = {}

    static defaultProps = {}

    state = {
        mobileDetailRecord: null
    }

    openMobileDetail = (record) => this.setState({ mobileDetailRecord: record })
    closeMobileDetail = () => this.setState({ mobileDetailRecord: null })

    components = {
        body: {
            row: DragableBodyRow,
            cell: EditableCell
        },
    }

    findSpecialInstructionByPosition = (pos) => {
        const { lineItems, specialInstructionEntities } = this.props
        const lineItemSpecialInstruction = lineItems.map((lineItem) => {
            const specialInstructionId = lineItem.get('special_instruction')
            if (lineItem.get('position') === pos) {
                return specialInstructionEntities.getIn([`${specialInstructionId}`, 'special_instruction'])
            }

            return null
        })

        return _.compact(lineItemSpecialInstruction.toJS())[0]
    }

    getColumns () {
        const { editable, i18n, refetchOrder } = this.props

        return [{
            title: i18n.t('inquiries:number'),
            key: 'No',
            width: '180px',
            dataIndex: 'position',
            editable: editable,
            render: (position) => (
                <div>
                    {position}
                    {this.findSpecialInstructionByPosition(position) && (
                        <span style={{ marginLeft: '3px' }}>(มี)</span>
                    )}
                </div>
            )
        }, {
            title: i18n.t('inquiries:lineItemNameTh'),
            dataIndex: 'name_th',
            editable: editable,
            width: '220px',
            render: (name, record) => {
                if (record.item_active !== false) { return name }

                return (
                    <div>
                        <div>{name}</div>
                        <Tag color="red">{i18n.t('inquiries:inactiveItem')}</Tag>
                    </div>
                )
            }
        }, {
            title: i18n.t('inquiries:lineItemNameEn'),
            dataIndex: 'name_en',
            editable: editable,
            width: '220px'
        }, {
            key: 'copyName',
            render: (lineItem) => {
                return (
                    <CopyToClipboard text={lineItem.name + ' = ' + lineItem.quantity + ' ' + lineItem.unit}>
                        <Button type="dashed" icon={<CopyOutlined />} shape="round" size="small" />
                    </CopyToClipboard>
                )
            },
            width: '50px'
        }, {
            title: i18n.t('inquiries:quantity'),
            dataIndex: 'quantity',
            key: 'quantity',
            editable: editable,
            width: '100px',
            render: (dataIndex, record) => {
                return (
                    <div>
                        <div>{dataIndex}</div>
                        <div>
                            {record.transfer_back_quantity ? <Tag>{record.transfer_back_quantity}</Tag> : null }
                        </div>
                    </div>
                )
            }
        }, {
            title: i18n.t('inquiries:unit'),
            dataIndex: 'unit',
            editable: editable,
            width: '120px'
        }, {
            title: i18n.t('inquiries:cost'),
            dataIndex: 'cost',
            editable: 'true',
            width: '140px'
        }, {
            title: i18n.t('inquiries:weightPerUnit'),
            dataIndex: 'weight',
            editable: editable,
            width: '140px'
        }, {
            title: i18n.t('inquiries:totalWeight'),
            dataIndex: 'total_weight',
            key: 'total_weight',
            width: '140px',
            render: (dataIndex, record) => {
                if (!record.total_weight_after_adjustment) { return dataIndex }

                return (
                    <div className="editable-cell-value-wrap"
                        style={{ paddingRight: 24 }}
                        onClick={this.handleToggleEdit}
                    >
                        <Typography.Text delete>{dataIndex}</Typography.Text>
                        <Tag color={dataIndex > record.total_weight_after_adjustment ? 'red' : 'green'}>
                            {record.total_weight_after_adjustment}
                        </Tag>
                    </div>
                )
            }
        }, {
            title: i18n.t('inquiries:unitPrice'),
            dataIndex: 'single_price',
            key: 'single_price',
            editable: editable,
            width: '110px',
            render: (dataIndex, record) => {
                if (!record.recommended_price) { return dataIndex }

                const currentPrice = _.toNumber(dataIndex)
                const recommendedPrice = _.toNumber(record.recommended_price)
                const badgeColor = currentPrice > recommendedPrice ? 'red' : 'default'

                return (
                    <div>
                        <div>{dataIndex}</div>
                        <div>
                            <Tag color={badgeColor}>
                                ฿ {recommendedPrice}
                            </Tag>
                        </div>
                    </div>
                )
            }
        }, {
            title: i18n.t('inquiries:totalPrice'),
            dataIndex: 'total_price',
            key: 'totalPrice',
            width: '110px',
            render: (dataIndex, record) => {
                if (!record.total_price_after_adjustment) { return dataIndex }

                return (
                    <div className="editable-cell-value-wrap" style={{ paddingRight: 24 }}>
                        <Typography.Text delete>{dataIndex}</Typography.Text>
                        <Tag color={_.toInteger(dataIndex) > record.total_price_after_adjustment ? 'red' : 'green'}>
                            {record.total_price_after_adjustment}
                        </Tag>
                    </div>
                )
            }
        }, {
            title: i18n.t('inquiries:itemState'),
            key: 'item_state',
            width: '150px',
            render: (record) => {
                return (
                    <ItemState itemState={record.item_state}
                        refetchOrder={refetchOrder}
                        itemExternalId={record.item_external_id} />
                )
            }
        }, {
            title: i18n.t('inquiries:action'),
            key: 'action',
            className: editable ? "show" : "hide",
            render: (record) => {
                // Hide delete button for service line items
                if (record.type === 'service_line_item') return null

                return (
                    <Popconfirm title={i18n.t('inquiries:deleteConfirm')}
                        onConfirm={() => this.handleDelete(record.id)}>
                        <Button type="link" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                )
            },
            width: '100px'
        }]
    }

    getCustomColumns () {
        return this.getColumns().map((col) => {
            if (!col.editable) { return col }

            if (col.dataIndex === "cost" || col.dataIndex === "single_price" || col.dataIndex === "weight") {
                col.editable = true

                return {
                    ...col,
                    onCell: record => {
                        return {
                            record,
                            editable: record.type === 'service_line_item' ? false : col.editable,
                            dataIndex: col.dataIndex,
                            title: col.title
                        }
                    }
                }
            }

            return {
                ...col,
                onCell: record => {
                    return {
                        record,
                        editable: record.type === 'service_line_item' ? false : (record.has_any_supplier_line_item ? false : col.editable),
                        dataIndex: col.dataIndex,
                        title: col.title
                    }
                }
            }
        })
    }

    handleDelete = (id) => {
        const { deleteOrderLineItem, orderId } = this.props

        deleteOrderLineItem(orderId, id)
    }

    handleMovePosition = (id, position) => {
        const { movePositionLineItem, orderId } = this.props
        const lineItem = {
            id: id,
            position: position
        }

        movePositionLineItem(orderId, id, lineItem)
    }

    moveRow = (dragIndex, hoverIndex) => {
        const data = flattenLineItems(this.props.lineItems.toJS())

        const dragRow = data[dragIndex]
        const lineItemId = dragRow.id
        
        // Find the target regular line item at hoverIndex
        let targetRegularLineItemIndex = 0
        let currentRegularItemCount = 0

        for (let i = 0; i <= hoverIndex && i < data.length; i++) {
            if (data[i].type !== 'service_line_item') {
                if (i === hoverIndex) {
                    targetRegularLineItemIndex = currentRegularItemCount
                    break
                }
                currentRegularItemCount++
            } else if (i === hoverIndex) {
                // If hovering over a service item, use the position of its parent line item
                targetRegularLineItemIndex = currentRegularItemCount - 1
                break
            }
        }
        
        // Calculate new position (1-based)
        const newPosition = targetRegularLineItemIndex + 1

        this.handleMovePosition(lineItemId, newPosition)
    }

    renderItemSuppliers = (record) => {
        return _.get(record, 'item.supplier_items', []).map((supplier) => {
            const stockStatus = _.get(supplier, 'stock_status')
            return (
                <Tag color="blue" key={supplier.id} style={{ verticalAlign: 'middle' }}>
                    <div style={{ textAlign: 'center' }}>{_.get(supplier, 'supplier_name', '')}</div>
                    <div style={{ textAlign: 'center' }}>{_.get(supplier, 'price', '')}</div>
                    <div style={{ textAlign: 'center' }}>{stockStatus}</div>
                    <div style={{ textAlign: 'center' }}>
                        {_.get(supplier, 'supplier_line_group', '')}
                        {_.get(supplier, 'supplier_line_group') && (
                            <CopyToClipboard text={_.get(supplier, 'supplier_line_group', '')}>
                                <Button type="link" icon={<CopyOutlined />} size="small" style={{ padding: '0 4px' }} />
                            </CopyToClipboard>
                        )}
                    </div>
                    <div style={{ textAlign: 'center' }}>{_.get(supplier, 'full_remark', '')}</div>
                </Tag>
            )
        })
    }

    renderProductLink = (record) => {
        const itemId = _.get(record, 'item_external_id')

        return _.get(record, 'item_product_paths', []).map((itemProduct) => {
            const url = _.get(itemProduct, 'url')
            const separator = url && url.includes('?') ? '&' : '?'
            const href = itemId
                ? `${apiConfig.apiBase}${url}${separator}item_id=${itemId}`
                : `${apiConfig.apiBase}${url}`

            return (
                <div key={url}>
                    <LinkOutlined style={{ paddingRight: '4px' }} />
                    <a target="_blank" href={href} rel="noreferrer">
                        {_.get(itemProduct, 'name', 'url')}
                    </a>
                </div>
            )
        })
    }

    renderSpecialInstruction = (record) => {
        const { i18n, inquiry, specialInstructionEntities } = this.props
        const lineItemsSaving = inquiry.get('lineItemsSaving')
        const columns = [{
            key: 'line-item-item-truck-image',
            dataIndex: 'lineItem',
            width: 180,
            render: (lineItem) => {
                return (
                    <>
                        <LineItemItemTruckImagesContainer
                            key="item-truck-images"
                            lineItem={lineItem}
                        />

                        <UploadItemTruckImageContainer
                            key="upload"
                            lineItem={lineItem}
                        />
                    </>
                )
            }
        }, {
            title: i18n.t('inquiries:specialInstruction'),
            editable: true,
            dataIndex: 'special_instruction',
            render: (specialInstruction) => <div>{specialInstruction.detail}</div>
        }]

        const dataSource = [
            {
                key: 'special_instruction',
                special_instruction: {
                    id: specialInstructionEntities.getIn([`${record.special_instruction}`, 'id']),
                    detail: specialInstructionEntities.getIn([`${record.special_instruction}`, 'special_instruction']),
                    lineItemId: record.id
                },
                lineItem: record
            }
        ]

        const customColumns = columns.map((col) => {
            if (!col.editable) { return col }

            return {
                ...col,
                onCell: record => {
                    return {
                        record,
                        editable: col.editable,
                        dataIndex: col.dataIndex,
                        title: col.title
                    }
                }
            }
        })

        return (
            <div>
                <Table
                    loading={lineItemsSaving}
                    columns={customColumns}
                    components={this.components}
                    dataSource={dataSource}
                    pagination={false}
                    rowClassName={() => 'editable-row'}
                    scroll={{ x: 1300 }}
                />
                <div>
                    {i18n.t('inquiries:suppliers')} {this.renderItemSuppliers(record)}
                </div>
                { record.item_external_id && (
                    <div>
                        <a
                            href={`https://www.onestockhome.com/th/admin/items/${record.item_external_id}`}
                            target="_blank" rel="noopener noreferrer"
                        >
                            Admin item page
                        </a>
                    </div>
                )}
                <div>
                    {i18n.t('inquiries:truckOriginAddresses')} {this.renderTruckOriginAddresses(record)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {record.item_external_id && (
                        <ItemProductImage itemExternalId={record.item_external_id} />
                    )}
                    <div>
                        {this.renderProductLink(record)}
                    </div>
                </div>
            </div>
        )
    }

    renderTruckOriginAddresses = (record) => {
        return _.get(record, 'item.truck_origin_addresses', []).map(truck_origin_address => {
            return (
                <Tag color="blue" key={truck_origin_address.id}>
                    {truck_origin_address.name}
                </Tag>
            )
        })
    }

    renderMobileList = (data) => {
        const { editable, i18n } = this.props

        return (
            <List
                className='line-items-mobile-list'
                dataSource={data}
                rowKey='id'
                renderItem={(record) => {
                    const isService = record.type === 'service_line_item'
                    const isInactive = record.item_active === false
                    const totalPrice = record.total_price_after_adjustment || record.total_price

                    return (
                        <Card size='small'
                            className={`line-item-card ${isService ? 'is-service' : ''}`}
                            style={{ marginBottom: 8 }}>
                            <div className='card-row card-header'>
                                <span className='position'>#{record.position}</span>
                                <span className='name'>{record.name_th || record.name_en || '-'}</span>
                                {isInactive && (
                                    <Tag color='red' style={{ flexShrink: 0 }}>{i18n.t('inquiries:inactiveItem')}</Tag>
                                )}
                                {editable && !isService && (
                                    <Popconfirm title={i18n.t('inquiries:deleteConfirm')}
                                        onConfirm={() => this.handleDelete(record.id)}>
                                        <Button type='link' size='small' danger icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                )}
                            </div>
                            {record.name_en && record.name_th && (
                                <div className='card-row subtle'>{record.name_en}</div>
                            )}
                            <div className='card-row card-stats'>
                                <span>
                                    <Typography.Text type='secondary'>{i18n.t('inquiries:quantity')}: </Typography.Text>
                                    {record.quantity} {record.unit}
                                </span>
                                <span>
                                    <Typography.Text type='secondary'>{i18n.t('inquiries:unitPrice')}: </Typography.Text>
                                    ฿{record.single_price}
                                </span>
                                <span>
                                    <Typography.Text type='secondary'>{i18n.t('inquiries:totalPrice')}: </Typography.Text>
                                    <strong>฿{totalPrice}</strong>
                                </span>
                            </div>
                            {!isService && (
                                <div className='card-row card-meta'>
                                    <ItemState itemState={record.item_state}
                                        refetchOrder={this.props.refetchOrder}
                                        itemExternalId={record.item_external_id} />
                                    <CopyToClipboard text={record.name + ' = ' + record.quantity + ' ' + record.unit}>
                                        <Button type='dashed' size='small' icon={<CopyOutlined />} shape='round'>
                                            copy
                                        </Button>
                                    </CopyToClipboard>
                                    <Button type='link' size='small'
                                        icon={<InfoCircleOutlined />}
                                        onClick={() => this.openMobileDetail(record)}>
                                        {i18n.t('inquiries:moreDetail') || 'รายละเอียดเพิ่มเติม'}
                                    </Button>
                                </div>
                            )}
                        </Card>
                    )
                }}
            />
        )
    }

    render () {
        const { editable, handleChangePagination, i18n, inquiry, isMobile, lineItems, toggleItemPricesModal } = this.props

        const lineItemsWithService = flattenLineItems(lineItems.toJS())

        if (isMobile) {
            const { mobileDetailRecord } = this.state

            return (
                <DndProvider backend={HTML5Backend}>
                    <LineItemsWrapper>
                        <div className='mobile-edit-hint'>
                            <Typography.Text type='secondary'>
                                แก้ไขและจัดเรียงใหม่ได้ที่หน้าจอใหญ่ขึ้น
                            </Typography.Text>
                        </div>
                        {this.renderMobileList(lineItemsWithService)}
                        <Modal centered
                            footer={[
                                <Button key='back' onClick={toggleItemPricesModal}>
                                    {i18n.t('shared:close')}
                                </Button>,
                            ]}
                            onCancel={toggleItemPricesModal}
                            style={{ top: 10 }}
                            open={inquiry.get('showItemPricesModal')}
                            width={'95%'}>
                            <ItemPricesContainer itemId={inquiry.get('itemId')} />
                        </Modal>
                        <Modal
                            title={mobileDetailRecord ? `#${mobileDetailRecord.position} ${mobileDetailRecord.name_th || mobileDetailRecord.name_en || ''}` : ''}
                            open={!!mobileDetailRecord}
                            onCancel={this.closeMobileDetail}
                            footer={[
                                <Button key='close' onClick={this.closeMobileDetail}>
                                    {i18n.t('shared:close')}
                                </Button>
                            ]}
                            width={'95%'}
                            style={{ top: 10 }}
                            destroyOnClose>
                            {mobileDetailRecord && this.renderSpecialInstruction(mobileDetailRecord)}
                        </Modal>
                    </LineItemsWrapper>
                </DndProvider>
            )
        }

        return (
            (<DndProvider backend={HTML5Backend}>
                <LineItemsWrapper>
                    <Table
                        columns={this.getCustomColumns()}
                        components={this.components}
                        dataSource={lineItemsWithService}
                        loading={inquiry.get('lineItemsLoading')}
                        onRow={(_record, index) => ({
                            index,
                            moveRow: editable ? this.moveRow : null
                        })}
                        expandable={{
                            expandedRowRender: this.renderSpecialInstruction,
                            rowExpandable: (record) => record.type !== 'service_line_item'
                        }}
                        rowClassName={() => 'editable-row'}
                        rowKey="id"
                        onChange={handleChangePagination}
                        pagination={{
                            ...inquiry.get('lineItemsPagination').toJS(),
                            showSizeChanger: false
                        }}
                        style={{ marginBottom: 24 }}
                        scroll={{ x: 1300 }}
                    />
                    <Modal centered
                        footer={[
                            <Button key="back" onClick={toggleItemPricesModal}>
                                {i18n.t('shared:close')}
                            </Button>,
                        ]}
                        onCancel={toggleItemPricesModal}
                        style={{ top: 10 }}
                        open={inquiry.get('showItemPricesModal')}
                        width={900}>
                        <ItemPricesContainer itemId={inquiry.get('itemId')} />
                    </Modal>
                </LineItemsWrapper>
            </DndProvider>)
        );
    }
}

DragSortingTable.propTypes = {
    orderId: PropTypes.string.isRequired,
    lineItems: ImmutablePropTypes.list,
    editable: PropTypes.bool,
    handleChangePagination: PropTypes.func.isRequired
}

const mapStateToProps = state => ({
    inquiry: state.get('inquiry'),
    lineItemEntities: state.getIn(['Entities', 'lineItems']),
    specialInstructionEntities: state.getIn(['Entities', 'specialInstructions']),
})

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators({
        deleteOrderLineItem,
        movePositionLineItem,
        toggleItemPricesModal
    }, dispatch)
}

const ConnectedTable = connect(mapStateToProps, mapDispatchToProps)(withTranslation('inquiries')(DragSortingTable))

const InquiryLineItemsWithBreakpoint = (props) => {
    const screens = Grid.useBreakpoint()
    const isMobile = !screens.md
    return <ConnectedTable {...props} isMobile={isMobile} />
}

export default InquiryLineItemsWithBreakpoint
